import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useKeyboard from '../../../shared/hooks/useKeyboard';

/**
 * Rapid Fire drill: binary A-or-B timed questions from matrix data.
 * "OSPF or EIGRP: Which has a default AD of 90?"
 */
export default function RapidFire({ matrix, onComplete }) {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [results, setResults] = useState([]);
  const [showFeedback, setShowFeedback] = useState(false);
  const [lastAnswer, setLastAnswer] = useState(null);

  // Generate questions from matrix attributes
  const questions = useMemo(() => {
    if (!matrix || matrix.protocols.length < 2) return [];

    const qs = [];
    // Prioritize exam_hot attributes
    const sortedAttrs = [...matrix.attributes].sort((a, b) => (b.exam_hot ? 1 : 0) - (a.exam_hot ? 1 : 0));

    for (const attr of sortedAttrs) {
      const protocols = matrix.protocols.filter(p => attr.values[p] && attr.values[p] !== '—');
      if (protocols.length < 2) continue;

      // For each unique value, create a "which protocol has this?" question
      const valueMap = {};
      for (const p of protocols) {
        const v = attr.values[p];
        if (!valueMap[v]) valueMap[v] = [];
        valueMap[v].push(p);
      }

      // Only create questions where protocols differ
      const uniqueValues = Object.entries(valueMap).filter(([_, ps]) => ps.length < protocols.length);

      for (const [value, correctProtocols] of uniqueValues) {
        const wrongProtocol = protocols.find(p => !correctProtocols.includes(p));
        if (!wrongProtocol || correctProtocols.length > 1) continue;

        const correct = correctProtocols[0];
        // Randomly order the two choices
        const choices = Math.random() > 0.5 ? [correct, wrongProtocol] : [wrongProtocol, correct];

        qs.push({
          question: `Which has **${attr.name}**: ${value}?`,
          choices,
          correct,
          attribute: attr.name,
          exam_hot: attr.exam_hot,
        });
      }
    }

    // Shuffle and limit to 20 questions
    return shuffleArray(qs).slice(0, 20);
  }, [matrix]);

  const currentQ = questions[currentIndex];

  const handleAnswer = useCallback((answer) => {
    if (showFeedback || !currentQ) return;

    const isCorrect = answer === currentQ.correct;
    setLastAnswer({ answer, isCorrect });
    setResults(prev => [...prev, { ...currentQ, userAnswer: answer, isCorrect }]);
    setShowFeedback(true);

    // Auto-advance after brief feedback
    setTimeout(() => {
      setShowFeedback(false);
      setLastAnswer(null);
      if (currentIndex + 1 < questions.length) {
        setCurrentIndex(prev => prev + 1);
      } else {
        // Drill complete
      }
    }, 1200);
  }, [showFeedback, currentQ, currentIndex, questions.length]);

  // Keyboard: 1 or left arrow for first choice, 2 or right arrow for second
  useKeyboard({
    '1': () => currentQ && handleAnswer(currentQ.choices[0]),
    '2': () => currentQ && handleAnswer(currentQ.choices[1]),
    'ArrowLeft': () => currentQ && handleAnswer(currentQ.choices[0]),
    'ArrowRight': () => currentQ && handleAnswer(currentQ.choices[1]),
  }, !showFeedback && currentIndex < questions.length);

  // No questions available
  if (questions.length === 0) {
    return (
      <div className="max-w-lg mx-auto px-4 py-12 text-center">
        <p className="text-gray-500 mb-4">Not enough distinct values to generate drill questions for this comparison.</p>
        <button onClick={() => navigate(-1)} className="text-blue-600 hover:text-blue-700">← Back</button>
      </div>
    );
  }

  // Drill complete
  if (currentIndex >= questions.length && !showFeedback) {
    const correct = results.filter(r => r.isCorrect).length;
    const accuracy = Math.round((correct / results.length) * 100);

    return (
      <div className="max-w-lg mx-auto px-4 py-12 text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Drill Complete!</h2>
        <div className="text-5xl font-bold mb-2" style={{
          color: accuracy >= 80 ? '#2ECC71' : accuracy >= 60 ? '#F39C12' : '#E74C3C'
        }}>
          {accuracy}%
        </div>
        <p className="text-gray-500 mb-6">{correct} of {results.length} correct</p>
        <div className="flex justify-center gap-3">
          <button
            onClick={() => { setCurrentIndex(0); setResults([]); }}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            Try Again
          </button>
          <button
            onClick={() => navigate(`/matrix/${matrix.id}`)}
            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
          >
            Back to Matrix
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-4 py-12">
      {/* Progress */}
      <div className="flex justify-between text-sm text-gray-400 mb-2">
        <span>Question {currentIndex + 1} / {questions.length}</span>
        <span>{results.filter(r => r.isCorrect).length} correct</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-1.5 mb-8">
        <div
          className="bg-green-500 h-1.5 rounded-full transition-all"
          style={{ width: `${(currentIndex / questions.length) * 100}%` }}
        />
      </div>

      {/* Question */}
      <div className="text-center mb-8">
        {currentQ.exam_hot && (
          <span className="text-xs text-yellow-600 bg-yellow-50 px-2 py-0.5 rounded-full mb-2 inline-block">
            ★ Exam-critical
          </span>
        )}
        <h3 className="text-xl font-semibold text-gray-900">
          {currentQ.question.replace(/\*\*/g, '')}
        </h3>
      </div>

      {/* Two big choice buttons */}
      <div className="grid grid-cols-2 gap-4">
        {currentQ.choices.map((choice, i) => {
          let btnClass = 'bg-white border-2 border-gray-300 text-gray-900 hover:border-blue-500 hover:bg-blue-50';

          if (showFeedback && lastAnswer) {
            if (choice === currentQ.correct) {
              btnClass = 'bg-green-50 border-2 border-green-500 text-green-800';
            } else if (choice === lastAnswer.answer && !lastAnswer.isCorrect) {
              btnClass = 'bg-red-50 border-2 border-red-500 text-red-800';
            }
          }

          return (
            <button
              key={choice}
              onClick={() => handleAnswer(choice)}
              disabled={showFeedback}
              className={`${btnClass} rounded-xl py-8 px-4 text-xl font-bold transition-all disabled:cursor-not-allowed`}
            >
              {choice}
              <span className="block text-xs font-normal mt-1 opacity-50">({i + 1})</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function shuffleArray(array) {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}
